import React, { useState, useRef } from "react";
import styled from "styled-components";
import {
  HeaderContainer,
  Logo,
  Nav,
  NavLink,
  HamburgerIcon,
  MobileMenu,
  CloseButton,
  ContactNumber,
  ContactNumberDesktop,
  DesktopNav,
  DesktopLink,
  DesktopLinkWrapper,
  DesktopDropdownOverlay,
  DropdownPanel,
  DropdownCopy,
  DropdownEyebrow,
  DropdownGallery,
  DropdownGalleryItem,
  DropdownHeading,
  DropdownMeta,
  DropdownText,
  DropdownTextBlock,
  MobileSubmenu,
  SubmenuToggle,
} from "./Header.styles";
import Hamburger from "../../Assets/Icons/hamburger.svg";
import plantImageOne from "../../Assets/Images/optimized/plant1-card.webp";
import plantImageTwo from "../../Assets/Images/optimized/plant2-card.webp";
import plantImageThree from "../../Assets/Images/optimized/plant3-card.webp";
import plantImageFour from "../../Assets/Images/optimized/plant4-card.webp";
import tipperImageOne from "../../Assets/Images/optimized/tipper1-card.webp";
import tipperImageTwo from "../../Assets/Images/optimized/tipper2-card.webp";
import tipperImageThree from "../../Assets/Images/optimized/tipper3-card.webp";
import tipperImageFour from "../../Assets/Images/optimized/tipper4-card.webp";
import wastePlanningImageOne from "../../Assets/Images/optimized/Planning1-opt.webp";
import wastePlanningImageTwo from "../../Assets/Images/optimized/Planning2-opt.webp";
import wastePlanningImageThree from "../../Assets/Images/optimized/Planning3-opt.webp";
import wastePlanningImageFour from "../../Assets/Images/optimized/Planning4-opt.webp";
import surveyingImageOne from "../../Assets/Images/optimized/surveying1-opt.webp";
import surveyingImageTwo from "../../Assets/Images/optimized/surveying2-opt.webp";
import restorationImageOne from "../../Assets/Images/optimized/Restoration1-opt.webp";
import restorationImageTwo from "../../Assets/Images/optimized/Restoration2-opt.webp";
import restorationImageThree from "../../Assets/Images/optimized/Restoration3-opt.webp";
import restorationImageFour from "../../Assets/Images/optimized/Restoration4-opt.webp";

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5); /* Semi-transparent black */
  z-index: 5;
  display: ${({ isOpen }) => (isOpen ? "block" : "none")};
`;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [dropdownOverlayTop, setDropdownOverlayTop] = useState(0);
  const closeTimer = useRef(null);
  const dropdownPanelRef = useRef(null);

  const toggleMenu = () => {
    setIsOpen((prevState) => {
      const newState = !prevState;
      document.body.style.overflow = newState ? "hidden" : "auto";
      return newState;
    });
  };

  const phoneNumber = "01962 717627";
  const disableMobileServiceNavigation = true;

  const plantHireGallery = [
    { src: plantImageOne, alt: "Plant hire image one" },
    { src: plantImageTwo, alt: "Plant hire image two" },
    { src: plantImageThree, alt: "Plant hire image three" },
    { src: plantImageFour, alt: "Plant hire image four" },
  ];

  const tipperHireGallery = [
    { src: tipperImageOne, alt: "Tipper hire image one" },
    { src: tipperImageTwo, alt: "Tipper hire image two" },
    { src: tipperImageThree, alt: "Tipper hire image three" },
    { src: tipperImageFour, alt: "Tipper hire image four" },
  ];

  const landfillRestorationDropdownCopy = [
    "Our landfill services are designed to provide efficient, safe, and environmentally responsible waste management solutions, with a focus on compliant operations, site preparation, waste compaction, and capping.",
    "Land restoration companies such as Inert Landfill & Restoration Management Limited specialise in transforming degraded land, including former quarries, mines, and landfills, into thriving ecological habitats, woodland, or green public spaces.",
    "We provide services like soil improvement, habitat creation, and biodiversity net gain (BNG).",
  ];

  const landfillRestorationGallery = [
    { src: restorationImageOne, alt: "Landfill Restoration 1" },
    { src: restorationImageTwo, alt: "Landfill Restoration 2" },
    { src: restorationImageThree, alt: "Landfill restoration image three" },
    { src: restorationImageFour, alt: "Landfill restoration image four" },
  ];

  const wastePlanningGallery = [
    { src: wastePlanningImageOne, alt: "Waste planning image one" },
    { src: wastePlanningImageTwo, alt: "Waste planning image two" },
    { src: wastePlanningImageThree, alt: "Waste planning image three" },
    { src: wastePlanningImageFour, alt: "Waste planning image four" },
  ];

  const wastePlanningDropdownCopy = [
    "Waste planning and permitting are the two critical regulatory hurdles for any waste management activity in the UK. While obtaining planning permission from a local authority focuses on the use of land and its impact on the community, an environmental permit from the Environment Agency focuses on how the facility is operated to prevent pollution and protect health.",
    "In addition, we now have the ability to use the CL:AIRE Definition of Waste Code of Practice. This voluntary framework enables the sustainable, legal reuse of excavated materials such as soil and aggregate on-site or between sites without needing environmental permits.",
    "It provides a consistent, audited process to demonstrate that materials are not waste, reducing landfill and costs.",
  ];

  const surveyingDropdownCopy = [
    "Inert Surveying and Design Limited undertake land surveys, define the shape of the land, and determine property boundaries.",
    "We use technology to gather, store, process, and deliver geographic information using tools like satellites and AI. The technologies we use include electronic surveying equipment, Global Navigation Satellite Systems (GNSS), drones, and 3D laser scanners.",
    "We offer topographical surveys, setting-out, boundary surveys, and 3D modeling or laser scanning services for construction projects, infrastructure development, legal documentation, and urban planning.",
  ];

  const surveyingGallery = [
    { src: surveyingImageOne, alt: "Surveying image one" },
    { src: surveyingImageTwo, alt: "Surveying image two" },
  ];

  const navItems = [
    {
      label: "Tipper Hire",
      href: "/tipper",
      dropdownContent: {
        eyebrow: "Bulk haulage and spoil removal across the South",
        title: "Tipper Hire",
        paragraphs: [
          "We work with a vast array of companies in the Hampshire / West Sussex and surrounding areas, providing an unrivalled service in removing spoil and rubble away from your site, be that as a groundworker, civil engineer, demolition firm, etc.",
          "Our network of approved restoration sites allow us to move whatever volume is required, with the minimum of fuss and inconvenience.",
        ],
        gallery: tipperHireGallery,
      },
    },
    {
      label: "Plant Hire",
      href: "/plant",
      dropdownContent: {
        eyebrow: "Plant available across a wide range of jobs",
        title: "Plant Hire",
        paragraphs: [
          "We own and operate a large selection of construction plant.",
          "We have a crusher, as well as numerous excavators, dozers and screeners.",
          "All of this plant is available, on terms that can be discussed, in many circumstances, both operated and self-drive.",
          "Please ring into the office for details.",
        ],
        gallery: plantHireGallery,
      },
    },
    {
      label: "Landfill & Restoration",
      href: "/landfill",
      dropdownContent: {
        eyebrow: "Landfill operations with restoration outcomes",
        title: "Landfill & Restoration",
        paragraphs: landfillRestorationDropdownCopy,
        gallery: landfillRestorationGallery,
      },
    },
    {
      label: "Planning",
      href: "/wasteplanning",
      dropdownContent: {
        eyebrow: "Planning, permitting, and compliant material reuse",
        title: "Planning",
        paragraphs: wastePlanningDropdownCopy,
        gallery: wastePlanningGallery,
      },
    },
    {
      label: "Surveying",
      href: "/surveying",
      dropdownContent: {
        eyebrow: "Measured data for construction and planning",
        title: "Surveying",
        paragraphs: surveyingDropdownCopy,
        gallery: surveyingGallery,
      },
    },
  ];

  const activeItem = hoveredIndex !== null ? navItems[hoveredIndex] : null;
  const activeDropdown = activeItem?.dropdownContent;

  const updateOverlayTop = () => {
    if (!dropdownPanelRef.current) return;
    const { bottom } = dropdownPanelRef.current.getBoundingClientRect();
    setDropdownOverlayTop(Math.max(0, Math.round(bottom)));
  };

  return (
    <>
      <HeaderContainer>
        <div className="OutsideWrapper">
          <Logo href="/">
            <img src="https://irp.cdn-website.com/39796b00/dms3rep/multi/inert.webp" alt="Company Logo" loading="eager" fetchPriority="high" decoding="async" />
          </Logo>
          <ContactNumber href={`tel:${phoneNumber}`} style={{ textDecoration: "none", color: "inherit" }}>
            {phoneNumber}
          </ContactNumber>
          <HamburgerIcon src={Hamburger} alt="menu" onClick={toggleMenu} />

          <div className="NavigationContainer">
            <Nav>
              <NavLink href="/about">About us</NavLink>
              <NavLink href="/health-safety">Health &amp; Safety</NavLink>
              <NavLink href="/news">News</NavLink>
              <NavLink href="/contact">Contact us</NavLink>
            </Nav>
            <ContactNumberDesktop href={`tel:${phoneNumber}`} style={{ textDecoration: "none", color: "inherit" }}>
              {phoneNumber}
            </ContactNumberDesktop>
          </div>
        </div>

        {isOpen && (
          <>
            <Overlay isOpen={isOpen} onClick={toggleMenu} />
            <MobileMenu>
              <CloseButton onClick={toggleMenu}>X</CloseButton>
              {navItems.map((item, idx) => (
                <div key={idx} style={{ width: "100%" }}>
                  {item.submenu ? (
                    <>
                      <SubmenuToggle onClick={() => setOpenMobileSubmenu(openMobileSubmenu === idx ? null : idx)}>{item.label}</SubmenuToggle>
                      {openMobileSubmenu === idx && (
                        <MobileSubmenu>
                          {item.submenu.map((sub, sidx) => (
                            <NavLink
                              key={sidx}
                              href={sub.href}
                              onClick={(e) => {
                                if (disableMobileServiceNavigation) {
                                  e.preventDefault();
                                  toggleMenu();
                                  return;
                                }
                                toggleMenu();
                              }}
                            >
                              {sub.label}
                            </NavLink>
                          ))}
                        </MobileSubmenu>
                      )}
                    </>
                  ) : (
                    <NavLink
                      href={item.href}
                      onClick={(e) => {
                        if (disableMobileServiceNavigation) {
                          e.preventDefault();
                          toggleMenu();
                          return;
                        }
                        toggleMenu();
                      }}
                    >
                      {item.label}
                    </NavLink>
                  )}
                </div>
              ))}
            </MobileMenu>
          </>
        )}
      </HeaderContainer>

      <DesktopNav>
        {navItems.map((item, idx) => {
          const hasDropdown = Boolean(item.dropdownContent);

          return (
            <DesktopLinkWrapper
              key={idx}
              onMouseEnter={() => {
                if (!hasDropdown) return;
                if (closeTimer.current) {
                  clearTimeout(closeTimer.current);
                  closeTimer.current = null;
                }
                setHoveredIndex(idx);
                setTimeout(updateOverlayTop, 0);
              }}
              onMouseLeave={() => {
                if (!hasDropdown) return;
                // delay closing to allow moving pointer to the panel
                if (closeTimer.current) clearTimeout(closeTimer.current);
                closeTimer.current = setTimeout(() => setHoveredIndex((prev) => (prev === idx ? null : prev)), 180);
              }}
            >
              <DesktopLink as={hasDropdown ? "button" : "a"} href={hasDropdown ? undefined : item.href} type={hasDropdown ? "button" : undefined} $isInteractive={true}>
                {item.label}
              </DesktopLink>
            </DesktopLinkWrapper>
          );
        })}

        {activeDropdown && (
          <DesktopDropdownOverlay
            $top={dropdownOverlayTop}
            onClick={() => {
              if (closeTimer.current) clearTimeout(closeTimer.current);
              setHoveredIndex(null);
            }}
          />
        )}

        {activeDropdown && (
          <DropdownPanel
            ref={dropdownPanelRef}
            onMouseEnter={() => {
              if (closeTimer.current) {
                clearTimeout(closeTimer.current);
                closeTimer.current = null;
              }
              updateOverlayTop();
            }}
            onMouseLeave={() => {
              if (closeTimer.current) clearTimeout(closeTimer.current);
              closeTimer.current = setTimeout(() => setHoveredIndex(null), 180);
            }}
          >
            <>
              <DropdownCopy>
                <DropdownEyebrow>{activeDropdown.eyebrow}</DropdownEyebrow>
                <DropdownHeading>{activeDropdown.title}</DropdownHeading>
                <DropdownTextBlock>
                  {activeDropdown.paragraphs.map((paragraph) => (
                    <DropdownText key={paragraph}>{paragraph}</DropdownText>
                  ))}
                </DropdownTextBlock>
                <DropdownMeta href={`tel:${phoneNumber}`}>Call the office on {phoneNumber}</DropdownMeta>
              </DropdownCopy>

              {activeDropdown.gallery?.length ? (
                <DropdownGallery>
                  {activeDropdown.gallery.map((image) => (
                    <DropdownGalleryItem key={image.alt}>
                      <img src={image.src} alt={image.alt} loading="lazy" decoding="async" />
                    </DropdownGalleryItem>
                  ))}
                </DropdownGallery>
              ) : null}
            </>
          </DropdownPanel>
        )}
      </DesktopNav>
    </>
  );
};

export default Header;
